import React, { useMemo } from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { PositionType, PositionTypeString } from "app/models/position";
import { convertDateTimeToDMY, convertWithFormat } from "_metronic/_helpers";
import {
  PositionHistoryData,
  PositionHistoryProps,
  ChartPoint,
} from "./interfaces";
import { IMonitoring } from "app/models/monitoring";
import { Box, Button } from "@material-ui/core";
import UpdateIcon from "@material-ui/icons/Update";
import {
  getFixedClockDecubitusPositionForDate,
  getFixedClockDecubitusNextPositionTimeForDate,
  getDynamicClockDecubitusPositionForDate,
  getDynamicClockDecubitusNextPositionTimeForDate,
  getDecubitusPositionColor,
  getDecubitusPositionColorDarker,
} from "app/models/position";

/**
 * Props specific to PositionHistoryLineChart
 * -----------------------------------------------------------
 * monitoring?: IMonitoring
 *   - Monitoring object for context. Used to determine allowed positions.
 *   - If provided, will highlight data points in red if their position is not in monitoring.allowedPositionsList.
 */
export interface PHLCSpecificProps {
  monitoring?: IMonitoring;
  onActivateRealTime?: () => void;
  onDeactivateRealTime?: () => void;
}

/**
 * ChartPointWithColor
 * -------------------
 * Extends ChartPoint with an optional fillColor property for custom coloring.
 */
interface ChartPointWithColor extends ChartPoint {
  fillColor?: string;
}

/**
 * PositionHistoryLineChart
 * -----------------------------------------
 * A chart that shows patient position durations.
 *
 * - X Axis -> Time (HH:mm:ss).
 * - Y Axis -> Patient positions (labels).
 * - Series -> Stepline segments representing position durations.
 * - Points not in allowedPositionsList are colored red.
 * - Expected positions are now displayed using ranged annotations (x and x2).
 */
function PositionHistoryLineChart({
  intl,
  data,
  width,
  height,
  monitoring,
  onActivateRealTime,
  onDeactivateRealTime,
}: PositionHistoryProps & PHLCSpecificProps & WrappedComponentProps) {
  // Real time toggle
  const [isRealTimeActive, setIsRealTimeActive] = React.useState(true);
  const handleRealTimeToggle = () => {
    if (isRealTimeActive) {
      onDeactivateRealTime?.();
    } else {
      onActivateRealTime?.();
    }
    setIsRealTimeActive(!isRealTimeActive);
  };

  // List of valid position types, in fixed order.
  const positionTypeList = useMemo(
    () => [
      PositionType.DV,
      PositionType.DLE_DV,
      PositionType.DLE,
      PositionType.DLD_DV,
      PositionType.DDH,
      PositionType.DDH_DLE,
      PositionType.DLD,
      PositionType.DDH_DLD,
      PositionType.DDE,
      PositionType.DDH_DDE,
      PositionType.SED,
      PositionType.DDE_SED,
      PositionType.FALL_RAISED,
      PositionType.UPSIDE_DOWN,
    ],
    []
  );

  // Mapping from position string to numeric index.
  const positionTypesMappingTextToId = useMemo(() => {
    const map = new Map<string, number>();
    positionTypeList.forEach((value, index) => {
      map.set(value, index);
    });
    return map;
  }, [positionTypeList]);

  // Prepare the series data with fillColor for each point
  const allowedPositionsSet = monitoring?.allowedPositions
    ? new Set(monitoring.allowedPositions)
    : null;

  const seriesData: ChartPointWithColor[] = (data || [])
    .reduce(
      (
        acc: ChartPointWithColor[],
        current: PositionHistoryData,
        index: number
      ) => {
        const prevId = data[index - 1]?.positionId;
        const currentId = current.positionId;
        const nextId = data[index + 1]?.positionId;

        // Skip redundant middle points if same before and after
        if (currentId === prevId && currentId === nextId) {
          return acc;
        }

        // Skip INV (invalid position) and UNK (unknown position) completely
        if (currentId === PositionType.INV || currentId === PositionType.UNK) {
          return acc;
        }

        const mappedId = positionTypesMappingTextToId.get(currentId);
        if (mappedId !== undefined) {
          // Determine fillColor: red if not allowed, blue if allowed
          let fillColor;
          if (allowedPositionsSet) {
            fillColor = allowedPositionsSet.has(currentId)
              ? "#1976d2"
              : "#e53935";
          }
          acc.push({ x: current.timestamp, y: mappedId, fillColor });
        }

        return acc;
      },
      []
    )
    .sort((a, b) => (a.x < b.x ? -1 : 1)); // Ensure sorted by timestamp

  // === [START] Y Axis Configuration ===

  const yAxisConfig: ApexYAxis = {
    min: 0,
    max: positionTypeList.length - 1,
    tickAmount: positionTypeList.length,
    forceNiceScale: true,
    labels: {
      formatter: (val: number) => {
        const id = positionTypeList[Math.round(val)] ?? PositionType.UNK;
        return intl.formatMessage({ id: `POSITIONS.${id}.CHART` });
      },
      style: {
        fontSize: "10px",
        fontWeight: "bold",
      },
    },
  };

  // === [END] Y Axis Configuration ===

  // === [START] X Axis Configuration ===

  const minTimestamp = seriesData[0]?.x;
  const maxTimestamp = seriesData[seriesData.length - 1]?.x;
  const annotationLines: XAxisAnnotations[] = [];
  const annotationRanges: XAxisAnnotations[] = [];

  if (minTimestamp !== undefined && maxTimestamp !== undefined) {
    // Vertical Bars (Time Reference) Every 2 Hours
    // ? Remove for now...
    // const twoHoursMs = 2 * 60 * 60 * 1000;
    // for (
    //   let ts = Math.ceil(minTimestamp / twoHoursMs) * twoHoursMs;
    //   ts < maxTimestamp;
    //   ts += twoHoursMs
    // ) {
    //   annotationLines.push({
    //     x: ts,
    //     borderColor: "#bbb",
    //     strokeDashArray: 2,
    //     label: {
    //       text: "",
    //       style: {
    //         fontSize: "10px",
    //         color: "#888",
    //         background: "#fff",
    //       },
    //       orientation: "vertical",
    //     },
    //   });
    // }

    // Vertical Ranged Annotations (Expected Patient Position)
    if (
      monitoring?.pressureInjury &&
      monitoring.pressureInjury.enabled === "1"
    ) {
      const clockType = monitoring.pressureInjury.clockType || "fixed";
      const intervalMinutes = monitoring.pressureInjury.interval || 120;
      const monitoringStartDate = new Date(monitoring.checkin);

      // For now, hardcode the interval to 2 hours...
      // const intervalHours = intervalMinutes / 60;
      const intervalHours = 2;

      let nextPositionDate: Date | null = null;
      if (clockType === "fixed") {
        nextPositionDate = getFixedClockDecubitusNextPositionTimeForDate(
          new Date(minTimestamp),
          intervalHours
        );
      } else if (clockType === "dynamic") {
        nextPositionDate = getDynamicClockDecubitusNextPositionTimeForDate(
          monitoringStartDate,
          new Date(minTimestamp),
          intervalHours
        );
      }

      let lastPositionDate = new Date(minTimestamp);
      while (nextPositionDate && nextPositionDate.getTime() <= maxTimestamp) {
        let positionName: PositionTypeString | undefined = undefined;
        if (clockType === "fixed") {
          positionName = getFixedClockDecubitusPositionForDate(
            lastPositionDate
          );
        } else if (clockType === "dynamic") {
          positionName = getDynamicClockDecubitusPositionForDate(
            monitoringStartDate,
            lastPositionDate
          );
        }

        let rangeColor: string;
        let rangeColorDarker: string;
        if (
          positionName === PositionType.DDH ||
          positionName === PositionType.DLE ||
          positionName === PositionType.DLD
        ) {
          rangeColor = getDecubitusPositionColor(positionName);
          rangeColorDarker = getDecubitusPositionColorDarker(positionName);
        } else {
          rangeColor = "transparent";
          rangeColorDarker = "transparent";
        }

        annotationRanges.push({
          x: lastPositionDate.getTime(),
          x2: nextPositionDate.getTime(),
          fillColor: rangeColor,
          opacity: 0.25,
          label: {
            text: `${intl.formatMessage({
              id: `POSITIONS.${positionName}.CHART`,
            })} ↓`,
            style: {
              fontSize: "10px",
              fontWeight: "bold",
              color:
                rangeColorDarker === "transparent" ? "#444" : rangeColorDarker,
              background: "transparent",
            },
            offsetY: -50,
            offsetX: 10,
            borderWidth: 0,
          },
          borderWidth: 0,
        });

        lastPositionDate = new Date(nextPositionDate.getTime());
        if (clockType === "fixed") {
          nextPositionDate = getFixedClockDecubitusNextPositionTimeForDate(
            lastPositionDate,
            intervalHours
          );
        } else if (clockType === "dynamic") {
          nextPositionDate = getDynamicClockDecubitusNextPositionTimeForDate(
            monitoringStartDate,
            lastPositionDate,
            intervalHours
          );
        }
      }
    }
  }

  const xAxisConfig: ApexXAxis = {
    min: minTimestamp,
    max: maxTimestamp,
    tickAmount: 8,
    tickPlacement: "on",
    type: "datetime",
    labels: {
      style: {
        fontSize: "10px",
      },
      formatter: (ts: string) =>
        convertWithFormat(new Date(ts).getTime(), "dd/MM HH:mm:ss"),
      trim: false,
    },
  };

  // === [END] X Axis Configuration ===

  // ApexCharts configuration options.
  const options: ApexOptions = {
    chart: {
      id: "position-duration-bar",
      zoom: {
        enabled: true,
        type: "x",
      },
      selection: {
        enabled: true,
      },
      toolbar: {
        autoSelected: "zoom",
        tools: {
          pan: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          reset: true,
          selection: false,
          download: false,
        },
      },
      animations: {
        enabled: false,
      },
    },
    annotations: {
      xaxis: [...annotationLines, ...annotationRanges],
    },
    title: {
      text: intl.formatMessage({
        id: "CHART.PACIENT_HISTORY.POSITION.VERTICAL_BARS.TITLE",
      }),
      align: "center",
    },
    subtitle: {
      // Empty subtitle to create space between titles and chart
      text: "",
    },
    noData: {
      text: intl.formatMessage({
        id: "CHART.PACIENT_HISTORY.POSITION.NO_DATA",
      }),
    },
    tooltip: {
      shared: false,
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        if (!point)
          return `<div style="padding: 5px;">Nenhum dado disponível.</div>`;
        const date =
          point.x !== undefined
            ? convertDateTimeToDMY(new Date(point.x).getTime())
            : "N/A";
        const positionLabel =
          typeof point.y === "number"
            ? intl.formatMessage({
                id: `POSITIONS.${positionTypeList[point.y]}`,
              })
            : "N/A";
        return `<div style="padding: 5px;">
                  <span style="font-weight: bold;">Posição:</span> <span style="color: ${point.fillColor}";>${positionLabel}</span>
                  <br />
                  <span style="font-weight: bold;">Data:</span> ${date}
                </div>`;
      },
    },
    xaxis: xAxisConfig,
    yaxis: [
      // Labels on both sides
      { ...yAxisConfig, opposite: false },
      { ...yAxisConfig, opposite: true },
    ],
    stroke: {
      curve: "smooth",
      width: 2,
    },
    markers: {
      size: 6,
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: {
        size: 8,
      },
    },
  };

  return (
    <Box
      position={"relative"}
      style={{
        padding: "1rem 0",
      }}
    >
      <Button
        className="btn no-transform"
        style={{
          position: "absolute",
          top: 9,
          left: 12,
          zIndex: 2,
          height: 21,
          padding: 5,
          border: "1px solid #000000de",
        }}
        size="small"
        onClick={handleRealTimeToggle}
      >
        <UpdateIcon
          style={{
            fontSize: 16,
            marginRight: 4,
          }}
        />
        <span>
          {intl.formatMessage({
            id: "CHART.PACIENT_HISTORY.POSITION.REAL_TIME",
          })}
        </span>
        <span
          style={{
            color: isRealTimeActive ? "#27912b" : "#b92020c7",
            marginLeft: 4,
            marginRight: 1,
            fontWeight: "bold",
          }}
        >
          {isRealTimeActive
            ? intl.formatMessage({ id: "COMMON.FEMININE.ACTIVE" })
            : intl.formatMessage({ id: "COMMON.FEMININE.INACTIVE" })}
        </span>
      </Button>
      <Chart
        type="line"
        options={options}
        series={[
          {
            name: "Duration",
            data: seriesData,
          },
        ]}
        width={width ?? "100%"}
        height={height ?? "100%"}
      />
    </Box>
  );
}

export default injectIntl(PositionHistoryLineChart);
